import React, { useState, useEffect, useMemo } from 'react';
import { debounce } from 'lodash';
import { Billboard } from '@companycam/slab-web';

import { translate } from 'translations/i18n';
import toast from 'helpers/toast';
import {
  useGetPageTemplates,
  useDeletePageTemplate,
  useCreatePageFromTemplate,
  FILTER_KEYS,
} from 'javascripts/data/v3';
import useCreateExamplePage from '../hooks/useCreateExamplePage';
import { PageTemplateId } from 'data/v3/templates/pages/types';
import { useModal } from 'components/shared/hooks';
import NotesEmptyState from 'images/NotesEmptyState.png';
import PreviewContent from './PreviewContent';
import UseTemplateModal from 'components/projects/UseTemplateModal';
import { PageTemplateObject } from 'data/v3/data-contracts';
import { ExamplePageId } from '../examples/examplePages';
import Steven from 'helpers/steven';

export type UsePageTemplateModalProps = {
  projectDbId: number;
  projectTitle: string;
  showInvoiceFeedbackBanner: () => void;
};

type BillboardProps = {
  image: { alt: string; src: string } | null;
  title: string | null;
  message: string;
};

type ExampleTemplate = {
  id: string;
  title: string;
  isExample: boolean;
};

const UsePageTemplateModal = ({
  projectDbId,
  projectTitle,
  showInvoiceFeedbackBanner,
}: UsePageTemplateModalProps) => {
  const [previewTemplateId, setPreviewTemplateId] = useState(null);
  const { showModal, hideModal, Modal } = useModal();

  const [searchTerm, setSearchTerm] = useState('');

  const FILTERS = [
    {
      key: FILTER_KEYS.ALL,
      label: translate('common.all'),
    },
    {
      key: FILTER_KEYS.YOUR_COMPANY,
      label: translate('pages.filters.yourCompany'),
    },
    {
      key: FILTER_KEYS.EXAMPLES,
      label: translate('pages.filters.exampleTemplates'),
    },
  ];

  const [activeFilter, setActiveFilter] = useState(FILTERS[0]);

  const { recentTemplates, pageTemplates, isLoading, refetch, isError } =
    useGetPageTemplates({ searchTerm, activeFilter, includeExamples: true });

  const debouncedRefetch = useMemo(() => debounce(refetch, 1000), [refetch]);

  // On search changed, refetch the data
  useEffect(() => {
    if (searchTerm === '') {
      refetch();
    } else {
      debouncedRefetch();
    }
  }, [searchTerm, debouncedRefetch, refetch]);

  const deletePageTemplate = useDeletePageTemplate({
    onSuccess: () => {
      toast.success(translate('template.shared.templateDeletedSuccess'));
    },
    onError: () => {
      toast.error(translate('template.shared.templateDeletedError'));
    },
  });

  const handleShowPageModal = (pageDbId: number) => {
    showModal(Modal.PROJECT_PAGE_MODAL, {
      projectDbId,
      pageDbId,
    });
  };

  const createPageFromTemplate = useCreatePageFromTemplate({
    projectDbId,
    onSuccess: (page) => {
      toast.success(
        translate('pageTemplates.preview.createFromTemplateSuccess'),
      );
      handleShowPageModal(page.id);
    },
    onError: () => {
      toast.error(translate('template.shared.templateCreateError'));
    },
  });

  const { createExamplePage, isLoading: createExamplePageIsLoading } =
    useCreateExamplePage({
      projectDbId,
      projectTitle,
      showInvoiceFeedbackBanner,
      onSuccess: (page) => {
        toast.success(
          translate('pageTemplates.preview.createFromTemplateSuccess'),
        );
        handleShowPageModal(page.id);
      },
    });

  const handleCreateFromTemplate = (
    templateId: PageTemplateId | ExamplePageId,
  ) => {
    if (createExamplePageIsLoading) return;

    if (typeof templateId === 'string') {
      createExamplePage(templateId);
      return;
    }

    createPageFromTemplate.mutate({
      template_id: templateId,
    });
  };

  const handleDelete = (deletedId: PageTemplateId) => {
    deletePageTemplate.mutate({
      pageTemplateDbId: deletedId,
    });
  };

  const isSearching = searchTerm.length > 0;

  const billboardProps: BillboardProps = isSearching
    ? {
        image: null,
        title: null,
        message: translate('templates.shared.multiSelectModal.empty'),
      }
    : {
        image: {
          alt: translate('projectPage.usePageTemplateModal.emptyStateImageAlt'),
          src: NotesEmptyState,
        },
        title: translate('templates.shared.multiSelectModal.empty'),
        message: translate(
          'projectPage.usePageTemplateModal.emptyStateMessage',
        ),
      };

  const handlePreview = (id: PageTemplateId | ExamplePageId) => {
    setPreviewTemplateId(id);

    // fire event if example is previewed
    if (typeof id === 'string') {
      Steven.elementClicked({
        id: 'preview_example',
        content: 'useTemplateModal.previewButtonLabel',
        type: id,
        action: `preview_example_clicked`,
      });
    }
  };

  return (
    <UseTemplateModal<PageTemplateObject | ExampleTemplate>
      activeFilter={activeFilter}
      filters={FILTERS}
      setActiveFilter={(type) => {
        setActiveFilter(type);
      }}
      recentTemplates={recentTemplates}
      templates={pageTemplates}
      title={translate('projectPage.usePageTemplateModal.title')}
      onCreateFromTemplate={handleCreateFromTemplate}
      previewContent={
        previewTemplateId ? (
          <PreviewContent
            pageTemplateId={previewTemplateId}
            onCreateFromTemplate={handleCreateFromTemplate}
            onDelete={(deletedId: PageTemplateId) => handleDelete(deletedId)}
            onClose={() => setPreviewTemplateId(null)}
          />
        ) : null
      }
      onPreview={(id) => handlePreview(id)}
      onEdit={(id) => {
        hideModal();
        window.Turbo.visit(`/templates/pages/${id}/edit`);
      }}
      onDelete={(deletedId) => handleDelete(deletedId)}
      onSearchChanged={setSearchTerm}
      searchTerm={searchTerm}
      isLoading={isLoading}
      isError={isError}
      emptyState={
        <Billboard
          image={billboardProps.image}
          title={billboardProps.title}
          message={billboardProps.message}
        />
      }
      onCloseModal={hideModal}
    />
  );
};

export default UsePageTemplateModal;
